import { QueueService } from 'src/core/queue/domain/queue.service';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Storage } from 'src/core/storage/domain/storage';
import { StorableService } from 'src/core/storage/domain/storable-service';
import { Initializable } from 'src/core/common/domain/initializer/initializable';
import { Nullable } from 'src/core/common/domain/types/types';
import { Notification, NotificationPrimitives } from 'src/core/notifications/domain/notification';

type StoredNotifications = {
  last_page: number;
  notifications: Array<NotificationPrimitives>;
};

@Injectable({
  providedIn: 'root',
})
export class NotificationsService implements StorableService, Initializable {
  private readySubject = new BehaviorSubject<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/member-ordering
  ready$: Observable<boolean> = this.readySubject.asObservable();

  private readonly KEY_NOTIFICATIONS = 'notifications';
  private readonly KEY_UNREAD_COUNT = 'notifications_unread_count';
  private readonly unreadCountSubject = new BehaviorSubject<number>(0);
  private notifications: Array<Notification> = [];
  private lastStoredPage = 0;

  // eslint-disable-next-line @typescript-eslint/member-ordering
  unreadCount$: Observable<number> = this.unreadCountSubject.asObservable();

  constructor(private readonly storage: Storage, private readonly queueService: QueueService) {}

  async init(): Promise<void> {
    await this.initFromStorage();

    this.readySubject.next(true);
  }

  async clear(): Promise<void> {
    this.notifications = [];
    this.lastStoredPage = 0;
    this.unreadCountSubject.next(0);
    await this.storage.remove(this.KEY_NOTIFICATIONS);
    await this.storage.remove(this.KEY_UNREAD_COUNT);
  }

  getLastStoredPage(): number {
    return this.lastStoredPage;
  }

  getStoredNotifications(): Array<Notification> {
    return this.withLocalReadState(this.notifications);
  }

  withLocalReadState(notifications: Array<Notification>): Array<Notification> {
    const readIds = new Set([
      ...this.queueService.getPendingNotificationReadIds(),
      ...this.notifications.filter((notification) => notification.isRead).map((notification) => notification.id),
    ]);
    return notifications.map((notification) => (readIds.has(notification.id) ? notification.asRead() : notification));
  }

  hasPendingReads(): boolean {
    return this.queueService.getPendingNotificationReadIds().length > 0;
  }

  async replaceStoredNotifications(notifications: Array<Notification>): Promise<void> {
    await this.saveNotifications(notifications, 1);
  }

  // A page that does not follow the last stored one is ignored: the stored list was replaced by a newer
  // first page meanwhile (i.e. a sync while the modal kept paginating), and appending it would leave a gap.
  async appendStoredNotifications(page: number, notifications: Array<Notification>): Promise<void> {
    if (page !== this.lastStoredPage + 1) {
      return;
    }

    const storedIds = new Set(this.notifications.map((notification) => notification.id));
    const newNotifications = notifications.filter((notification) => !storedIds.has(notification.id));
    await this.saveNotifications([...this.notifications, ...newNotifications], page);
  }

  async markStoredAsRead(id: string): Promise<boolean> {
    const wasAlreadyRead = this.notifications.some((notification) => notification.id === id && notification.isRead);
    const notifications = this.notifications.map((notification) =>
      notification.id === id ? notification.asRead() : notification
    );
    await this.saveNotifications(notifications, this.lastStoredPage);
    return !wasAlreadyRead;
  }

  async markAllStoredAsRead(): Promise<void> {
    const notifications = this.notifications.map((notification) => notification.asRead());
    await this.saveNotifications(notifications, this.lastStoredPage);
  }

  getUnreadCount(): number {
    return this.unreadCountSubject.value;
  }

  async setUnreadCount(count: number): Promise<void> {
    const unreadCount = Math.max(0, count);
    this.unreadCountSubject.next(unreadCount);
    await this.storage.set(this.KEY_UNREAD_COUNT, unreadCount);
  }

  async decrementUnreadCount(): Promise<void> {
    await this.setUnreadCount(this.unreadCountSubject.value - 1);
  }

  private async initFromStorage(): Promise<void> {
    const stored: Nullable<StoredNotifications> = await this.storage.get(this.KEY_NOTIFICATIONS);
    this.notifications = (stored?.notifications ?? []).map((notification) => Notification.fromPrimitives(notification));
    this.lastStoredPage = stored?.last_page ?? 0;

    const unreadCount: number = (await this.storage.get(this.KEY_UNREAD_COUNT)) ?? 0;
    this.unreadCountSubject.next(unreadCount);
  }

  private async saveNotifications(notifications: Array<Notification>, lastPage: number): Promise<void> {
    this.notifications = [...notifications];
    this.lastStoredPage = lastPage;
    const stored: StoredNotifications = {
      last_page: lastPage,
      notifications: notifications.map((notification) => notification.toPrimitives()),
    };
    await this.storage.set(this.KEY_NOTIFICATIONS, stored);
  }
}
